export type DetailFieldType = 'text' | 'image' | 'pdf' | 'url' | 'long-text';

export interface DetailField {
    label: string;
    value: string;
    type: DetailFieldType;
    href?: string;
}

const SKIP_KEYS = new Set([
    '__typename',
    'password',
    'token',
    'hibernateLazyInitializer',
    'handler',
]);

/** Skip nested surrogate ids when parent object is already named */
const SKIP_NESTED_ID_PARENTS = new Set(['wallet', 'tier', 'shippingAddress', 'billingAddress', 'product', 'productBatch', 'pallet']);

const IMAGE_EXT = /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i;
const PDF_EXT = /\.pdf$/i;
const IMAGE_KEY = /photo|image|logo|avatar|thumbnail|picture/i;
const PDF_KEY = /pdf|certificate|licence|license|document/i;
const PATH_KEY = /path|file|upload/i;
const LONG_TEXT_THRESHOLD = 80;

/** Human-readable labels for API field keys */
const FIELD_LABELS: Record<string, string> = {
    id: 'ID',
    email: 'Email',
    accountId: 'Account ID',
    accountType: 'Account Type',
    businessName: 'Business Name',
    businessInfo: 'Business Info',
    businessLicenceNumber: 'Business Licence No.',
    businessLicenceExpirationDate: 'Licence Expiration',
    businessLicencePath: 'Business Licence',
    deaNumber: 'DEA Number',
    deaCertificatePath: 'DEA Certificate',
    resaleCertificatePath: 'Resale Certificate',
    statePharmacyLicencePath: 'State Pharmacy Licence',
    federalTaxId: 'Federal Tax ID',
    faxNumber: 'Fax',
    phoneNumber: 'Phone',
    primaryContactName: 'Primary Contact',
    pharmacyGln: 'Pharmacy GLN',
    supplierGln: 'Supplier GLN',
    supplierSgln: 'Supplier SGLN',
    isAccountStatus: 'Account Active',
    registrationDate: 'Registration Date',
    addressLine1: 'Address line 1',
    addressLine2: 'Address line 2',
    city: 'City',
    state: 'State',
    zipCode: 'ZIP code',
    country: 'Country',
    name: 'Name',
    balance: 'Balance',
    tierType: 'Tier type',
    percentageTier: 'Tier discount (%)',
    orderDate: 'Order date',
    orderStatus: 'Order status',
    totalAmount: 'Total amount',
    finalAmount: 'Final amount',
    isPaid: 'Paid',
    paymentMethod: 'Payment method',
    shippingRecipientName: 'Recipient',
    signature: 'Signature',
    documentId: 'Document ID',
    productBatchId: 'Batch ID',
    salesOrderItemId: 'Sales order item ID',
    ndcCode: 'NDC code',
    itemName: 'Product name',
    lotNumber: 'Lot number',
    quantity: 'Quantity',
    dosage: 'Dosage',
    dosageName: 'Dosage',
    description: 'Description',
    status: 'Status',
    createdAt: 'Created',
    updatedAt: 'Updated',
    notifiedAt: 'Notified',
    expiryDate: 'Expiry date',
    comment: 'Comment',
    reference: 'Reference',
    externalReference: 'External reference',
    transactionDate: 'Transaction date',
    eventType: 'Event type',
    eventTimestamp: 'Event time',
    performedBy: 'Performed by',
    serialNumber: 'Serial number',
    productId: 'Product ID',
    path: 'File',
    productPhoto: 'Product photo',
    photoPath: 'Photo',
    jsonPathPresent: 'JSON on file',
    epcisGuidelineVersion: 'EPCIS guideline',
    epcisSchemaVersion: 'EPCIS schema',
    certFilename: 'Expected cert file',
    certSerial: 'Cert serial',
    certSubject: 'Cert subject',
    certStatus: 'Cert status',
    partnershipOutbound: 'Outbound route',
    partnershipInbound: 'Inbound route',
    as2Url: 'Receive URL',
    as2Id: 'AS2 ID',
    environment: 'Environment',
    openas2PartnerName: 'OpenAS2 name',
    partnerIp: 'Partner IP',
    sourceSchemaVersion: 'Stored EPCIS version',
    packagingInfo: 'Packaging info',
    purchaseOrderNumber: 'Purchase order',
    invoiceNumber: 'Invoice number',
    remotefilename: 'Remote file name',
    remotepath: 'Remote path',
    filesizebytes: 'File size',
    warehouseName: 'Warehouse',
    warehouseId: 'Warehouse ID',
    preferredSupplierEmail: 'Supplier email',
    preferredSupplierBusinessName: 'Supplier business',
    prefferedSupplierId: 'Supplier ID',
    packageBarcode: 'Package barcode',
    batchSize: 'Batch size',
    verify: 'VRS verified',
    verifierGln: 'Verifier GLN',
    verificationTime: 'Verification time',
    rxProduct: 'RX product',
    sscc: 'Pallet SSCC',
    weightKg: 'Pallet weight (kg)',
    lastInventoryType: 'Last inventory type',
    companyPrefix: 'Company prefix',
    itemReference: 'Item reference',
    productGtin: 'Product GTIN',
    productNdc: 'Product NDC',
    productName: 'Product name',
    actualPrice: 'Actual price',
    createdBy: 'Created by',
    updatedBy: 'Updated by',
};

/** Short group names for nested objects */
const GROUP_LABELS: Record<string, string> = {
    account: 'Account',
    shippingAddress: 'Shipping address',
    billingAddress: 'Billing address',
    wallet: 'Wallet',
    tier: 'Tier',
    product: 'Product',
    productBatch: 'Batch',
    productPhoto: 'Photo',
    order: 'Order',
    item: 'Line item',
    pallet: 'Pallet',
};

function formatLabel(key: string): string {
    if (FIELD_LABELS[key]) {
        return FIELD_LABELS[key];
    }
    return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/_/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Remove consecutive duplicate words: "Tier Tier Type" → "Tier Type" */
function dedupeWords(label: string): string {
    const words = label.split(/\s+/).filter(Boolean);
    const out: string[] = [];
    for (const w of words) {
        if (out.length === 0 || out[out.length - 1].toLowerCase() !== w.toLowerCase()) {
            out.push(w);
        }
    }
    return out.join(' ');
}

/**
 * Build a clean label from key path, e.g. ['account','shippingAddress','city'] → "Shipping address – City"
 */
function labelFromPath(path: string[]): string {
    if (path.length === 0) {
        return '';
    }

    const leafKey = path[path.length - 1];
    if (leafKey === 'lotNumber' && path.length === 1) {
        return 'Shipment ID (desadv)';
    }
    const leafLabel = formatLabel(leafKey);

    if (path.length === 1) {
        return leafLabel;
    }

    const parentKey = path[path.length - 2];
    const parentGroup = GROUP_LABELS[parentKey] ?? formatLabel(parentKey);

    // account.* — omit redundant "Account" on leaf when leaf already says it
    if (parentKey === 'account' && path.length === 2) {
        const leafLower = leafLabel.toLowerCase();
        if (leafLower.startsWith('account')) {
            return leafLabel;
        }
        return leafLabel;
    }

    // account.shippingAddress.* → group under shipping address
    if (path.length >= 3 && path[path.length - 3] === 'account' && parentKey === 'shippingAddress') {
        return dedupeWords(`${GROUP_LABELS.shippingAddress} – ${leafLabel}`);
    }

    if (path.length >= 3 && path[path.length - 3] === 'account' && parentKey === 'billingAddress') {
        return dedupeWords(`${GROUP_LABELS.billingAddress} – ${leafLabel}`);
    }

    if (parentKey === 'wallet') {
        return dedupeWords(`Wallet – ${leafLabel}`);
    }

    if (parentKey === 'tier') {
        if (leafKey === 'tierType') {
            return 'Tier type';
        }
        if (leafKey === 'percentageTier') {
            return 'Tier discount (%)';
        }
        return dedupeWords(`Tier – ${leafLabel}`);
    }

    if (parentKey === 'shippingAddress' || parentKey === 'billingAddress') {
        return dedupeWords(`${parentGroup} – ${leafLabel}`);
    }

  // Generic: group – field, drop duplicate words
    const combined = `${parentGroup} – ${leafLabel}`;
    const parentFirst = parentGroup.split(/\s+/)[0]?.toLowerCase() ?? '';
    const leafLower = leafLabel.toLowerCase();
    if (parentFirst && leafLower.startsWith(parentFirst)) {
        return dedupeWords(leafLabel);
    }

    return dedupeWords(combined);
}

function classifyField(key: string, rawValue: string): Pick<DetailField, 'type' | 'href'> {
    const lower = rawValue.toLowerCase();
    const keyLower = key.toLowerCase();

    // AS2 partner cert filenames are not upload paths — use admin reference-certificate API.
    if (keyLower === 'certfilename' || keyLower === 'cert_filename') {
        return { type: 'text' };
    }

    // AS2 message server paths/names live under /appdata/as2 — never /app/uploads. Show as
    // plain text; the raw XML/MDN is viewed via GET /admin/as2/messages/{id}/xml.
    const AS2_TEXT_FIELDS = new Set([
        'filepath',
        'outboxfilename',
        'mdnmessageid',
        'messageid',
        'idempotencykey',
        'correlationid',
    ]);
    const INTEGRATION_TEXT_FIELDS = new Set([
        'remotefilename',
        'remotepath',
        'localstoragepath',
        'filesizebytes',
    ]);
    if (AS2_TEXT_FIELDS.has(keyLower) || INTEGRATION_TEXT_FIELDS.has(keyLower)) {
        return rawValue.length > LONG_TEXT_THRESHOLD ? { type: 'long-text' } : { type: 'text' };
    }

    if (rawValue.startsWith('http://') || rawValue.startsWith('https://')) {
        if (IMAGE_EXT.test(lower)) {
            return { type: 'image', href: rawValue };
        }
        if (PDF_EXT.test(lower) || lower.includes('.pdf')) {
            return { type: 'pdf', href: rawValue };
        }
        if (lower.includes('/uploads/') || lower.includes('/app/uploads/')) {
            if (PDF_EXT.test(lower) || lower.includes('.pdf')) {
                return { type: 'pdf', href: rawValue };
            }
            return { type: 'image', href: rawValue };
        }
        if (rawValue.length > LONG_TEXT_THRESHOLD) {
            return { type: 'long-text', href: rawValue };
        }
        return { type: 'url', href: rawValue };
    }

    const looksLikeFilePath =
        PATH_KEY.test(keyLower) ||
        lower.includes('uploads/') ||
        lower.includes('upload/') ||
        IMAGE_EXT.test(lower) ||
        PDF_EXT.test(lower);

    if (looksLikeFilePath || IMAGE_KEY.test(keyLower)) {
        if (PDF_EXT.test(lower) || (PDF_KEY.test(keyLower) && !IMAGE_EXT.test(lower))) {
            return { type: 'pdf', href: rawValue };
        }
        if (IMAGE_EXT.test(lower) || IMAGE_KEY.test(keyLower)) {
            return { type: 'image', href: rawValue };
        }
        if (PATH_KEY.test(keyLower) && rawValue.length > 3) {
            if (PDF_KEY.test(keyLower)) {
                return { type: 'pdf', href: rawValue };
            }
            return { type: 'image', href: rawValue };
        }
    }

    if (keyLower.includes('certsubject') || keyLower.includes('fingerprint')) {
        if (rawValue.length > 40) {
            return { type: 'long-text' };
        }
    }

    if (rawValue.length > LONG_TEXT_THRESHOLD) {
        return { type: 'long-text' };
    }

    return { type: 'text' };
}

function formatValue(value: unknown, key?: string): string {
    if (value === null || value === undefined || value === '') {
        return '—';
    }
    if (key?.toLowerCase() === 'filesizebytes' && typeof value === 'number') {
        const bytes = value;
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }
    if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
    }
    if (typeof value === 'number') {
        return String(value);
    }
    if (typeof value === 'string') {
        if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
            try {
                return new Date(value).toLocaleString();
            } catch {
                return value;
            }
        }
        return value;
    }
    if (Array.isArray(value)) {
        if (value.length === 0) {
            return '—';
        }
        return value.map((v) => formatValue(v)).join(', ');
    }
    if (typeof value === 'object') {
        const obj = value as Record<string, unknown>;
        const display = obj.name ?? obj.label ?? obj.title;
        if (display !== undefined && display !== null && display !== '') {
            return String(display);
        }
        return '—';
    }
    return String(value);
}

function shouldSkipKey(key: string, path: string[], val: unknown): boolean {
    if (SKIP_KEYS.has(key)) {
        return true;
    }
    if (key === 'id' && path.length > 1 && SKIP_NESTED_ID_PARENTS.has(path[path.length - 2])) {
        return true;
    }
    if (val !== null && typeof val === 'object' && !Array.isArray(val)) {
        return false;
    }
    return false;
}

function buildDetailField(label: string, key: string, val: unknown): DetailField | null {
    const value = formatValue(val, key);
    if (value === '—') {
        return null;
    }
    const { type, href } = classifyField(key, value);
    return { label, value, type, href };
}

export function flattenRecordForDetail(
    record: unknown,
    path: string[] = [],
    depth = 0,
    maxDepth = 3
): DetailField[] {
    if (record === null || record === undefined) {
        return [];
    }

    if (typeof record !== 'object' || Array.isArray(record)) {
        const label = labelFromPath(path.length ? path : ['value']);
        const field = buildDetailField(label, path[path.length - 1] ?? 'value', record);
        return field ? [field] : [];
    }

    const fields: DetailField[] = [];
    const obj = record as Record<string, unknown>;

    for (const [key, val] of Object.entries(obj)) {
        if (shouldSkipKey(key, path, val)) {
            continue;
        }

        const nextPath = [...path, key];

        if (val !== null && typeof val === 'object' && !Array.isArray(val) && depth < maxDepth) {
            fields.push(...flattenRecordForDetail(val, nextPath, depth + 1, maxDepth));
        } else {
            const label = labelFromPath(nextPath);
            const field = buildDetailField(label, key, val);
            if (field) {
                fields.push(field);
            }
        }
    }

    const seen = new Set<string>();
    return fields.filter((f) => {
        const k = `${f.label}|${f.value}`;
        if (seen.has(k)) {
            return false;
        }
        seen.add(k);
        return true;
    });
}
